import { I18nAndL10n } from '@application/i18n/mod';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import weekDay from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { useMemo } from 'react';

dayjs.extend(isBetween);
dayjs.extend(weekDay);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(duration);
dayjs.extend(customParseFormat);

export enum DateDisplayFormats {
	DEFAULT,
	FULL_DISPLAY_IN_CURRENT_YEAR_NO_WEEKDAY,
	FULL_DISPLAY_NO_CURRENT_YEAR_AND_WEEKDAY,
	FULL_DISPLAY_NO_CURRENT_YEAR_WEEKDAY_TIME,
	FULL_DISPLAY_NO_TIME_WEEKDAY,
	FULL_DISPLAY_NO_TIME,
	JUST_TIME,
}

interface DateFormatOptions {
	includeTimePrefix?: boolean;
}

function newDate(date?: Date | string | DateTime, format?: string): VisibaDate {
	if (typeof date === 'string') {
		if (date === '') {
			return dayjs();
		}

		date = parseStringToUtc(date);
	}

	return dayjs(date, date ? format : undefined);
}

function isValidDate(date?: Date | string | DateTime): boolean {
	if (date == null) return false;

	return dayjs(date).isValid();
}

function getLocaleSpecificDateFormatTemplate(format = DateDisplayFormats.DEFAULT, options: DateFormatOptions = {}): string {
	let template = dayjs().localeData().longDateFormat('LLLL');
	const includeTimePrefix = options.includeTimePrefix !== false;

	switch (format) {
		case DateDisplayFormats.FULL_DISPLAY_IN_CURRENT_YEAR_NO_WEEKDAY: {
			template = template
				.split(' ')
				.filter((part) => !part.match(/[yY]{4}/g))
				.join(' ')
				.replace('dddd,', 'dddd')
				.replace('dddd', '');
			break;
		}
		case DateDisplayFormats.FULL_DISPLAY_NO_TIME_WEEKDAY: {
			template = template
				.split(' ')
				.filter((part) => !part.match(/[Hh]{2}|[m]{2}|[s]{2}|\[kl.\]|[A]{1}/g))
				.join(' ')
				.replace('dddd,', 'dddd')
				.replace('dddd', '')
				.replace('YYYY,', 'YYYY');
			break;
		}
		case DateDisplayFormats.FULL_DISPLAY_NO_CURRENT_YEAR_WEEKDAY_TIME: {
			template = template
				.split(' ')
				.filter((part) => !part.match(/[Hh]{2}|[m]{2}|[s]{2}|\[kl.\]|[A]{1}/g))
				.join(' ')
				.replace('dddd,', 'dddd')
				.replace('dddd', '')
				.replace('YYYY,', 'YYYY')
				.replace('YYYY', '');
			break;
		}
		case DateDisplayFormats.FULL_DISPLAY_NO_TIME: {
			template = template
				.split(' ')
				.filter((part) => !part.match(/[Hh]{2}|[m]{2}|[s]{2}|\[kl.\]|[A]{1}/g))
				.join(' ')
				.replace('YYYY,', 'YYYY');
			break;
		}
		case DateDisplayFormats.JUST_TIME: {
			template = template
				.split(' ')
				.filter((part) => part.match(/[Hh]{2}|[m]{2}|[s]{2}|\[kl.\]|[A]{1}/g))
				.join(' ');
			break;
		}
		case DateDisplayFormats.FULL_DISPLAY_NO_CURRENT_YEAR_AND_WEEKDAY: {
			template = template.replace('YYYY,', 'YYYY').replace('YYYY', '').replace('dddd,', 'dddd').replace('dddd', '');
			break;
		}
	}

	if (!includeTimePrefix) {
		template = template.replace(/\[kl\.\]\s?/g, '').trim();
	}

	return template;
}

function parseStringToUtc(dateStr: string): string {
	if (/[zZ]$|[+-]\d{2}:\d{2}$/.test(dateStr)) {
		return dateStr;
	}

	return `${dateStr}Z`;
}

function isYesterday(date: Dayjs): boolean {
	return date.isSame(newDate().subtract(1, 'day'), 'day');
}

function isToday(date: Dayjs): boolean {
	return date.isSame(newDate(), 'day');
}

function isTomorrow(date: Dayjs): boolean {
	return date.isSame(newDate().add(1, 'day'), 'day');
}

function isThisWeek(date: Dayjs): boolean {
	return date.isSame(newDate(), 'week');
}

function isThisYear(date: Dayjs): boolean {
	return date.isSame(newDate(), 'year');
}

async function loadLocale(locale: I18nAndL10n.AvailableVisibaLocales): AsyncResult<void> {
	try {
		if (locale === 'sv') {
			await import('dayjs/locale/sv');
		} else if (locale === 'nb') {
			await import('dayjs/locale/nb');
		} else if (locale === 'fi') {
			await import('dayjs/locale/fi');
		} else if (locale === 'da') {
			await import('dayjs/locale/da');
		} else if (locale === 'en-gb') {
			await import('dayjs/locale/en-gb');
		} else if (locale === 'ar') {
			await import('dayjs/locale/ar');
		} else if (locale === 'fa') {
			await import('dayjs/locale/fa');
		} else if (locale === 'hr') {
			await import('dayjs/locale/hr');
		} else if (locale === 'pt-br') {
			await import('dayjs/locale/pt-br');
		} else if (locale === 'nl') {
			await import('dayjs/locale/nl');
		}

		dayjs.locale(locale);

		return Ok();
	} catch {
		console.error(`Unexpected locale error loading, the provided locale "${locale}" is not supported in dayjs`);

		return Ok();
	}
}

function convertExternalLocaleToLocal(external: string): I18nAndL10n.AvailableVisibaLocales {
	const externalLowercase = external.toLocaleLowerCase();

	if (externalLowercase === 'en') return 'en-gb';
	if (externalLowercase === 'no') return 'nb';

	const availableLocales: I18nAndL10n.AvailableVisibaLocales[] = ['sv', 'nb', 'fi', 'da', 'en-gb', 'ar', 'fa', 'hr', 'pt-br'];

	if (availableLocales.includes(externalLowercase as I18nAndL10n.AvailableVisibaLocales)) {
		return externalLowercase as I18nAndL10n.AvailableVisibaLocales;
	}

	return 'en-gb';
}

export function useDate() {
	return useMemo(
		() => ({
			convertExternalLocaleToLocal,
			duration: dayjs.duration,
			getLocaleSpecificDateFormatTemplate,
			isThisWeek,
			isThisYear,
			isToday,
			isTomorrow,
			isValidDate,
			isYesterday,
			loadLocale,
			locale: dayjs.locale,
			newDate,
		}),
		[],
	);
}
